import { css } from '@emotion/react';
import { type FC, type HTMLAttributes, type ReactNode } from 'react';

import { Text } from '../Typography';

export type ComparisonColumn = {
  title: ReactNode;
  items: ReactNode[];
  /** `muted` marks each row with a cross; `highlight` with a check. */
  variant: 'muted' | 'highlight';
};

export type ComparisonCardsProps = HTMLAttributes<HTMLDivElement> & {
  columns: ComparisonColumn[];
};

const gridStyles = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-5);
`;

const cardStyles = {
  muted: css`
    background: var(--color-base-white);
    border: 1px solid var(--border-primary);
  `,
  highlight: css`
    /* A light tint of the brand green; there is no primary-50 token. */
    background: color-mix(in srgb, var(--color-primary-100) 40%, white);
    border: 2px solid var(--color-primary-500);
    box-shadow: 0 16px 32px rgba(25, 45, 30, 0.08);
  `,
};

const cardBase = css`
  padding: var(--spacing-7) var(--spacing-8);
  border-radius: 20px;
`;

const listStyles = css`
  margin: var(--spacing-2) 0 0;
  padding: 0;
  list-style: none;
`;

const rowStyles = (variant: ComparisonColumn['variant']) => css`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-3);
  padding: 14px 0;
  font-size: 16px;
  line-height: 1.5;
  color: ${variant === 'highlight'
    ? 'var(--color-primary-800)'
    : 'var(--color-neutral-700)'};
  font-weight: ${variant === 'highlight' ? 600 : 400};

  & + & {
    border-top: 1px solid
      ${variant === 'highlight'
        ? 'var(--color-primary-100)'
        : 'var(--border-primary)'};
  }

  svg {
    flex: 0 0 auto;
    margin-top: 2px;
  }
`;

const Cross = () => (
  <svg
    aria-hidden="true"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--color-neutral-500)"
    strokeWidth="2.4"
    strokeLinecap="round"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const Check = () => (
  <svg
    aria-hidden="true"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--color-primary-500)"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

/**
 * Side-by-side cards comparing two ways of doing something. The highlighted
 * column is the recommended one.
 */
const ComparisonCards: FC<ComparisonCardsProps> = ({
  columns,
  className,
  ...rest
}) => (
  <div {...rest} className={className} css={gridStyles}>
    {columns.map((column, index) => (
      <section key={index} css={[cardBase, cardStyles[column.variant]]}>
        <Text
          as="h3"
          size="sm"
          fontWeight="extrabold"
          color={
            column.variant === 'highlight'
              ? 'var(--color-primary-600)'
              : 'var(--color-neutral-700)'
          }
          css={{
            margin: 0,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {column.title}
        </Text>
        <ul css={listStyles}>
          {column.items.map((item, itemIndex) => (
            <li key={itemIndex} css={rowStyles(column.variant)}>
              {column.variant === 'highlight' ? <Check /> : <Cross />}
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>
    ))}
  </div>
);

ComparisonCards.displayName = 'ComparisonCards';

export default ComparisonCards;
